import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList, Cell,
} from "recharts";
import { PRIMARY, STEEL, MUTED, FG1, GOLD, FG3 } from "../../lib/funnel/theme";
import { fmtN, pctVal } from "../../lib/funnel/helpers";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { BaselineRow } from "./sql";
import { FUNNEL_STEPS } from "./helpers";

// Vertical-column funnel, plain stage bars without a stage-to-stage drop-off
// overlay (intentional — the overlay was distracting on this dashboard).

const STAGE_COLOURS: Record<string, string> = {
  "Signed up":             MUTED,
  "Connected calendar":    PRIMARY,
  "First transcript":      "#3F8898",
  "First summary":         "#5A8FA0",
  "2 summaries (14d)":     "#5F6FA0",
  "Custom template (14d)": STEEL,
  "Converted to paid":     "#3a7fb0",
};

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const renderTopLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x);
  const y = num(props.y);
  const w = num(props.width);
  const v = num(props.value);
  if (x == null || y == null || w == null || v == null) return null;
  if (v <= 0) return null;
  return (
    <text x={x + w / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={FG1}>
      {format(v)}
    </text>
  );
};

type StageDatum = {
  stage: string;
  count: number;
  pct: number;
  value: number;
  compareCount?: number;
  comparePct?: number;
  compareValue?: number;
};

const CHART_MARGIN = { top: 64, right: 32, left: 16, bottom: 36 };
const CHART_HEIGHT = 460;

export function BaselineFunnel({
  users,
  pagePills,
  compareUsers,
  comparePills,
}: {
  users: BaselineRow[];
  pagePills?: FilterPillSpec[];
  compareUsers?: BaselineRow[] | null;
  comparePills?: FilterPillSpec[];
}) {
  const [showAbs, setShowAbs] = useState(false);
  const compareOn = !!compareUsers;

  const stageData = useMemo(() => {
    const total = users.length;
    const compareTotal = compareUsers?.length ?? 0;
    const stages: StageDatum[] = FUNNEL_STEPS.map((step) => {
      const count = users.filter(step.predicate).length;
      const pct = pctVal(count, total);
      const datum: StageDatum = {
        stage: step.label,
        count,
        pct,
        value: showAbs ? count : pct,
      };
      if (compareUsers) {
        const cc = compareUsers.filter(step.predicate).length;
        const cp = pctVal(cc, compareTotal);
        datum.compareCount = cc;
        datum.comparePct = cp;
        datum.compareValue = showAbs ? cc : cp;
      }
      return datum;
    });
    return { total, compareTotal, stages };
  }, [users, compareUsers, showAbs]);

  const fmtVal = (n: number): string => (showAbs ? fmtN(Math.round(n)) : `${n.toFixed(1)}%`);
  const fmtAxis = (n: number): string => (showAbs ? fmtN(Math.round(n)) : `${n.toFixed(0)}%`);
  const yMax = showAbs ? Math.max(stageData.total, stageData.compareTotal, 1) : 100;

  const pills: FilterPillSpec[] = compareOn
    ? [
        ...(pagePills ?? []),
        { kind: "mode", label: "vs comparison" },
        ...(comparePills ?? []),
      ]
    : (pagePills ?? []);

  return (
    <section className="card">
      <SectionCardHeader
        title="Activation by milestone"
        subtitle={compareOn
          ? "Cumulative % reaching each milestone. Teal = baseline, gold = comparison."
          : "Cumulative % of the baseline cohort that reached each milestone."}
        filterPills={pills}
        headerExtra={<ValueToggle showAbs={showAbs} onChange={setShowAbs} />}
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={stageData.stages} margin={CHART_MARGIN} barCategoryGap={compareOn ? "20%" : "10%"}>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.15)" vertical={false} />
            <XAxis dataKey="stage" interval={0} tickLine={false} tick={{ fontSize: 12, fill: FG1 }} height={56} />
            <YAxis
              domain={[0, yMax]}
              tickFormatter={(v) => fmtAxis(Number(v))}
              tick={{ fontSize: 10, fill: MUTED }}
              width={showAbs ? 56 : 42}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              content={(props) => {
                const { active, payload, label } = props as unknown as { active?: boolean; payload?: { payload: StageDatum }[]; label?: string };
                if (!active || !payload || payload.length === 0) return null;
                const row = payload[0]?.payload;
                if (!row) return null;
                return (
                  <div style={{ background: "var(--white)", border: "1px solid var(--border-default)", borderRadius: 7, padding: "8px 10px", fontSize: 12, boxShadow: "var(--shadow-md)", lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                    <div style={{ color: PRIMARY }}>
                      Baseline: <strong>{row.pct.toFixed(1)}%</strong> ({fmtN(row.count)}/{fmtN(stageData.total)})
                    </div>
                    {compareOn && row.comparePct != null && (
                      <div style={{ color: GOLD, marginTop: 2 }}>
                        Comparison: <strong>{row.comparePct.toFixed(1)}%</strong> ({fmtN(row.compareCount ?? 0)}/{fmtN(stageData.compareTotal)})
                      </div>
                    )}
                    {compareOn && row.comparePct != null && (
                      <div style={{ color: FG3, marginTop: 4, fontSize: 11 }}>
                        Δ {(row.comparePct - row.pct >= 0 ? "+" : "")}{(row.comparePct - row.pct).toFixed(1)}pp
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {stageData.stages.map((d) => (
                <Cell key={d.stage} fill={STAGE_COLOURS[d.stage] ?? PRIMARY} />
              ))}
              <LabelList dataKey="value" content={renderTopLabel(fmtVal)} />
            </Bar>
            {compareOn && (
              <Bar dataKey="compareValue" radius={[4, 4, 0, 0]} isAnimationActive={false} fill={GOLD}>
                <LabelList dataKey="compareValue" content={renderTopLabel(fmtVal)} />
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ValueToggle({ showAbs, onChange }: { showAbs: boolean; onChange: (next: boolean) => void }) {
  return (
    <div role="group" aria-label="Value mode" style={{
      display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11,
    }}>
      {([
        { key: false, label: "%" },
        { key: true,  label: "#" },
      ] as const).map((opt) => {
        const active = showAbs === opt.key;
        return (
          <button
            key={String(opt.key)}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              padding: "3px 10px",
              background: active ? PRIMARY : "transparent",
              color: active ? "#fff" : FG1,
              border: "none",
              cursor: "pointer",
              fontWeight: active ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
